import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Share } from '@capacitor/share';
import { CardType } from 'src/app/enums/card-type';
import { Update } from 'src/app/interfaces/update';
import { UpdateAttachment } from 'src/app/interfaces/update-attachment';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { UpdateService } from 'src/app/services/update.service';
import { ApiInit } from 'src/app/utils/api-init';
import { CurrentWhiteLabelApplication } from 'src/app/utils/current-white-label-application';
import { DeviceService } from 'src/app/services/device.service';
import {
  IonRouterOutlet,
  NavController,
  Platform,
} from '@ionic/angular/standalone';
import { UpdateLikeType } from 'src/app/enums/update-like-type';
import { TranslateModule } from '@ngx-translate/core';
import { ContentTextPipe } from '../../../pipes/content-text.pipe';
import { PollComponent } from '../../../components/poll/poll.component';
import { SocialButtonsComponent } from '../../../components/social-button/social-buttons.component';
import { EventsComponent } from '../../../components/events/events.component';
import { TranslateTextDirective } from '../../../directives/translate-text.directive';
import { MediaSliderComponent } from '../../../components/media-slider/media-slider.component';
import { InlineSVGModule } from 'ng-inline-svg-2';
import { ImgFallBackDirective } from '../../../directives/img-fallback.directive';
import { NgIf, DatePipe } from '@angular/common';
import { LoaderComponent } from '../../../components/loader/loader.component';
import { IonContent, IonBackButton } from '@ionic/angular/standalone';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { Subscription } from 'rxjs';
import { DivClickDirective } from 'src/app/directives/div-click.directive';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
  selector: 'app-organisation-update',
  templateUrl: './organisation-update.page.html',
  standalone: true,
  imports: [
    LoaderComponent,
    NgIf,
    ImgFallBackDirective,
    ContentTextPipe,
    InlineSVGModule,
    MediaSliderComponent,
    TranslateTextDirective,
    EventsComponent,
    SocialButtonsComponent,
    PollComponent,
    DatePipe,
    ContentTextPipe,
    TranslateModule,
    IonContent,
    IonBackButton,
    DivClickDirective,
  ],
})
export class OrganisationUpdatePage implements OnDestroy {
  update: Update;
  CardType = CardType;
  appLanguage: string;
  images: UpdateAttachment[];
  canShare: boolean = false;
  defaultHref: string = '/';
  likedType: UpdateLikeType = null;
  public previewToken: string = null;

  private loadedUpdate: boolean = false;
  private customerSlug: string;
  private backSubscription: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private apiInit: ApiInit,
    private updateService: UpdateService,
    private route: ActivatedRoute,
    private app: CurrentWhiteLabelApplication,
    private deviceService: DeviceService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private navCtrl: NavController,
    private notificationService: NotificationService,
    ionRouterOutlet: IonRouterOutlet,
    platform: Platform
  ) {
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params.preview_token) {
        this.previewToken = params.preview_token;
      }
    });

    try {
      this.previewToken =
        this.activatedRoute.snapshot.queryParams.get('preview_token');
      this.loadUpdate(
        parseInt(this.activatedRoute.snapshot.paramMap.get('id'))
      );
      this.customerSlug = this.activatedRoute.snapshot.paramMap.get('slug');
    } catch (error) {
      // ignore
    }

    this.route.params.subscribe((params) => {
      if (params.slug && params.id) {
        try {
          this.loadUpdate(params.id);
          this.customerSlug = params.slug;
        } catch (error) {
          // ignore
        }
      }
    });

    this.backSubscription = platform.backButton.subscribeWithPriority(
      1,
      (processNextHandler) => {
        if (!ionRouterOutlet.canGoBack()) {
          this.openCustomer();
        } else {
          processNextHandler();
        }
      }
    );

    Share.canShare().then(
      (result) =>
        (this.canShare =
          result.value && !!app.getPwaDomain() && !this.previewToken)
    );
  }

  ngOnDestroy(): void {
    this.backSubscription.unsubscribe();
  }

  openCustomer() {
    this.navCtrl.setDirection('back');
    this.router.navigate(['organisations', this.customerSlug], {
      replaceUrl: true,
    });
  }

  async loadUpdate(updateId: number) {
    if (this.loadedUpdate) {
      return;
    }
    this.loadedUpdate = true;

    this.apiInit.watch(async () => {
      this.updateService
        .getUpdateById(updateId, this.previewToken)
        .then(async (update) => {
          this.update = update;
          if (this.update.status === 'CONCEPT' && this.previewToken) {
            // No need to load other update data
            return;
          }
          if (
            this.update.status === 'CONCEPT' ||
            new Date(this.update.publicationDate) > new Date()
          ) {
            let url = this.router.url.split('/projects')[0];
            this.router.navigate([url, '404'], { replaceUrl: true });
          } else {
            if (false === this.notificationService.isOpenedTroughNotification) {
              try {
                const response = await this.deviceService.getData(
                  new HttpParams().set('update_ids', this.update.id.toString())
                );
                if (response?.updates) {
                  this.likedType = response.updates[this.update.id].likedType;
                }
              } catch (error) {
                // ignore
              }
            }
            this.update.expanded = true;

            this.update.likesPerType =
              await this.updateService.getUpdateReactions(this.update);
            this.update.likedBy = Object.values(
              this.update.likesPerType
            ).reduce((c, n) => c + n, 0);
            this.update = { ...this.update };
          }

          this.analyticsService.logEvent(
            AnalyticsType.UPDATE_OPEN,
            {
              update_id: this.update.id,
              notification: this.notificationService.isOpenedTroughNotification,
            },
            this.update.project
          );
          this.notificationService.isOpenedTroughNotification = false;
        });
    });
  }

  share(event) {
    event.stopPropagation();
    Share.share({
      url: `https://${this.app.getPwaDomain()}/organisations/${
        this.customerSlug
      }/update/${this.update.id}`,
    }).catch((e) => {
      // share cancelled
    });
  }
}
